import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { qk } from '@/lib/queryKeys';
import type { Attachment } from './attachments';

export type ChannelSummary = {
  id: string;
  name: string;
  /** channel = ערוץ עם קהל. direct = שיחה אישית (מיגרציה 088). */
  kind: 'channel' | 'direct';
  description: string | null;
  is_announcement_only: boolean;
  on_shift_only: boolean;
  last_body: string | null;
  last_author: string | null;
  last_at: string | null;
  last_attachments: number;
  unread: number;
  pending_ack: number;
};

export type ChannelMessage = {
  id: string;
  channel_id: string;
  author_id: string;
  body: string | null;
  attachments: Attachment[];
  requires_ack: boolean;
  is_pinned: boolean;
  created_at: string;
  edited_at: string | null;
  employees: { first_name: string; last_name: string } | null;
  message_reads: { acknowledged_at: string | null }[];
  /** הודעה שנוצרה מ"הכר את המוצר" נושאת איתה את החידון שלה */
  product_spotlights: { id: string; quiz_id: string | null; product_id: string }[];
};

export const PAGE_SIZE = 30;

// ל messages יש שני מסלולים ל employees: author_id, ודרך message_reads.
// בלי ציון המפתח הזר PostgREST מחזיר 300 על שיוך דו משמעי.

export function useMyChannels() {
  return useQuery({
    queryKey: qk.channels,
    queryFn: async (): Promise<ChannelSummary[]> => {
      const { data, error } = await supabase.rpc('my_channels');
      if (error) throw error;
      return (data ?? []) as unknown as ChannelSummary[];
    },
  });
}

export type ChannelHeader = {
  id: string;
  name: string;
  kind: 'channel' | 'direct';
  description: string | null;
  is_announcement_only: boolean;
  on_shift_only: boolean;
};

/**
 * הכותרת של הערוץ שאני צופה בו.
 *
 * דרך RPC ולא קריאה ישירה לטבלה, כי בשיחה אישית השם המוצג הוא של
 * הצד השני, והוא שונה בין שני המשתתפים.
 */
export function useChannel(channelId: string) {
  return useQuery({
    queryKey: ['channel', channelId],
    queryFn: async (): Promise<ChannelHeader | null> => {
      const { data, error } = await supabase.rpc('channel_header', { p_channel: channelId });
      if (error) throw error;
      return (data ?? null) as unknown as ChannelHeader | null;
    },
  });
}

/**
 * 30 ההודעות האחרונות. טעינה הפוכה: שולפים את האחרונות ומציגים
 * בסדר כרונולוגי (מסמך התכנון 9.5).
 */
export function useMessages(channelId: string) {
  return useQuery({
    queryKey: qk.messages(channelId),
    queryFn: async (): Promise<ChannelMessage[]> => {
      const { data, error } = await supabase
        .from('messages')
        .select(
          `id, channel_id, author_id, body, attachments, requires_ack, is_pinned, created_at, edited_at,
           employees!messages_author_id_fkey ( first_name, last_name ),
           message_reads ( acknowledged_at ),
           product_spotlights ( id, quiz_id, product_id )`,
        )
        .eq('channel_id', channelId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE);
      if (error) throw error;
      return ((data ?? []) as unknown as ChannelMessage[]).reverse();
    },
  });
}

/**
 * זמן אמת רק איפה שצריך: ערוץ פתוח. האירוע לא מעדכן state ידנית,
 * הוא מפעיל invalidate על המפתח (מסמך התכנון 4.3).
 */
export function useChannelRealtime(channelId: string) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel(`messages:${channelId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'messages', filter: `channel_id=eq.${channelId}` },
        () => {
          void queryClient.invalidateQueries({ queryKey: qk.messages(channelId) });
          void queryClient.invalidateQueries({ queryKey: qk.channels });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [channelId, queryClient]);
}

export function useMessageMutations(channelId: string) {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: qk.messages(channelId) });
    void queryClient.invalidateQueries({ queryKey: qk.channels });
  };

  const send = useMutation({
    mutationFn: async ({
      body,
      authorId,
      requiresAck,
      attachments,
    }: {
      body: string;
      authorId: string;
      requiresAck: boolean;
      attachments: Attachment[];
    }) => {
      const { error } = await supabase.from('messages').insert({
        channel_id: channelId,
        author_id: authorId,
        body: body || null,
        attachments,
        requires_ack: requiresAck,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const ack = useMutation({
    mutationFn: async (messageId: string) => {
      const { error } = await supabase.rpc('ack_message', { p_message: messageId });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const markRead = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('mark_channel_read', { p_channel: channelId });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.channels }),
  });

  return { send, ack, markRead };
}

/**
 * לכמה אנשים תגיע הודעה בערוץ "רק במשמרת", אם תישלח עכשיו.
 *
 * מרועננת כל דקה: המספר משתנה כשמשמרת מתחילה או נגמרת, ומספר
 * מיושן כאן גרוע ממספר שאינו מוצג — הוא משכנע.
 */
export function useOnShiftReach(channelId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['channel-reach', channelId],
    enabled,
    refetchInterval: 60_000,
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase.rpc('on_shift_reach', { p_channel: channelId });
      if (error) throw error;
      return data ?? 0;
    },
  });
}
